import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Home,
  PieChart,
  Lock,
  ShieldCheck,
  FolderTree,
  UserPlus,
  Plus,
  Wallet,
  ClipboardList
} from 'lucide-react';
import { cn } from '../../lib/utils';
import HouseholdsScreen from './HouseholdsScreen';
import AccountsScreen from './AccountsScreen';
import ModelPortfoliosScreen from './ModelPortfoliosScreen';
import ClientOnboardingScreen from './ClientOnboardingScreen';
import SecurityManagementScreen from '../SecurityManagementScreen';
import ComplianceRulesScreen from '../ComplianceRulesScreen';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
`;

const TabsContainer = styled.div`
  background: linear-gradient(to bottom, rgba(15, 23, 42, 0.5) 0%, transparent 100%);
  padding: 1.5rem 2rem 0;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 4px;
  max-width: 1800px;
  margin: 0 auto;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const RefTab: React.FC<{
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: string;
}> = ({ icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={cn(
      "group flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all duration-200 relative",
      active
        ? "text-white"
        : "text-slate-400 hover:text-slate-200"
    )}
  >
    <span className={cn(
      "transition-transform duration-200 group-hover:scale-110",
      active ? "text-emerald-400" : "text-slate-500 group-hover:text-slate-300"
    )}>
      {icon}
    </span>
    {label}
    {badge && (
      <span className={cn(
        "text-[10px] px-1.5 py-0.5 rounded",
        active ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-700 text-slate-400"
      )}>
        {badge}
      </span>
    )}
    {active && (
      <motion.div
        layoutId="retail-ref-nav-indicator"
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"
      />
    )}
  </button>
);

const ContentContainer = styled.div`
  max-width: 1800px;
  margin: 0 auto;
`;

type TabType = 'households' | 'accounts' | 'onboarding' | 'models' | 'securities' | 'compliance';

const RetailReferenceScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('households');
  const [householdKey, setHouseholdKey] = useState(0);
  const [accountKey, setAccountKey] = useState(0);
  const [modelKey, setModelKey] = useState(0);
  const [onboardingKey, setOnboardingKey] = useState(0);

  // Callback to trigger new household modal
  const handleAddHousehold = () => {
    setHouseholdKey(prev => prev + 1);
  };

  // Callback to trigger new account modal
  const handleAddAccount = () => {
    setAccountKey(prev => prev + 1);
  };

  // Callback to trigger new model modal
  const handleAddModel = () => {
    setModelKey(prev => prev + 1);
  };

  // Get the action button based on active tab
  const renderActionButton = () => {
    switch (activeTab) {
      case 'households':
        return (
          <button
            onClick={handleAddHousehold}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Household
          </button>
        );
      case 'accounts':
        return (
          <button
            onClick={handleAddAccount}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Account
          </button>
        );
      case 'onboarding':
        return (
          <button
            onClick={() => setOnboardingKey(prev => prev + 1)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Start Onboarding
          </button>
        );
      case 'models':
        return (
          <button
            onClick={handleAddModel}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Model
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <TabsContainer>
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                <FolderTree className="w-6 h-6 text-emerald-500" />
                Client Setup
                <span className="ml-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs font-bold text-emerald-400 uppercase tracking-widest">
                  RIA
                </span>
              </h1>
              <p className="text-slate-500 text-sm font-medium mt-1">
                Manage households, accounts, model portfolios, and compliance rules
              </p>
            </div>
            <div className="flex gap-2">
              {renderActionButton()}
            </div>
          </div>

          <TabsWrapper>
            <RefTab
              icon={<Home className="w-4 h-4" />}
              label="Households"
              active={activeTab === 'households'}
              onClick={() => setActiveTab('households')}
            />
            <RefTab
              icon={<Wallet className="w-4 h-4" />}
              label="Accounts"
              active={activeTab === 'accounts'}
              onClick={() => setActiveTab('accounts')}
            />
            <RefTab
              icon={<ClipboardList className="w-4 h-4" />}
              label="Onboarding"
              active={activeTab === 'onboarding'}
              onClick={() => setActiveTab('onboarding')}
              badge="4"
            />
            <RefTab
              icon={<PieChart className="w-4 h-4" />}
              label="Models"
              active={activeTab === 'models'}
              onClick={() => setActiveTab('models')}
            />
            <RefTab
              icon={<Lock className="w-4 h-4" />}
              label="Securities"
              active={activeTab === 'securities'}
              onClick={() => setActiveTab('securities')}
            />
            <RefTab
              icon={<ShieldCheck className="w-4 h-4" />}
              label="Compliance Rules"
              active={activeTab === 'compliance'}
              onClick={() => setActiveTab('compliance')}
            />
          </TabsWrapper>
        </div>
      </TabsContainer>

      <ContentContainer>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'households' && <HouseholdsScreen embedded triggerNew={householdKey} />}
            {activeTab === 'accounts' && <AccountsScreen embedded triggerNew={accountKey} />}
            {activeTab === 'onboarding' && <ClientOnboardingScreen embedded triggerNew={onboardingKey} />}
            {activeTab === 'models' && <ModelPortfoliosScreen embedded triggerNew={modelKey} />}
            {activeTab === 'securities' && <SecurityManagementScreen />}
            {activeTab === 'compliance' && <ComplianceRulesScreen />}
          </motion.div>
        </AnimatePresence>
      </ContentContainer>

    </Container>
  );
};

export default RetailReferenceScreen;
